import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { qk } from '@/app/queryClient';
import { notificationService, type Notification } from '@/lib/api/services/notification.service';
import { cn } from '@/lib/cn';
import { formatDate } from '@/lib/format';
import { recordPath } from '@/lib/recordPath';
import { useRealtimeEvent } from '@/lib/realtime/socket';

function pathFor(n: Notification): string | null {
  return recordPath(n.relatedType, n.relatedId);
}

/**
 * The header notification bell: live unread badge plus a dropdown of recent
 * notifications. Clicking one marks it read and deep-links to the record it's
 * about (relatedType/relatedId) — a task assignment opens the task, an overdue
 * reminder the task, a won deal the deal, and so on.
 */
export function NotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { data: unread } = useQuery({
    queryKey: qk.notifications.unreadCount,
    queryFn: () => notificationService.unreadCount(),
    // Realtime push (below) is the live path; the poll is a fallback for when the
    // socket / Redis is unavailable, so it can be relaxed rather than tight.
    refetchInterval: 120_000,
    retry: false,
  });
  const count = unread?.count ?? 0;

  // Live delivery: the API publishes `notification:new` to this user's socket
  // room the instant a notification is raised. Reconcile against the server
  // (authoritative for read-state/ordering) rather than trusting the payload —
  // this refreshes the badge now, and the open dropdown list too.
  useRealtimeEvent('notification:new', () => {
    qc.invalidateQueries({ queryKey: qk.notifications.all });
  });

  const list = useQuery({
    queryKey: qk.notifications.list({ limit: 15 }),
    queryFn: () => notificationService.list({ limit: 15 }),
    enabled: open,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: qk.notifications.all });
  };

  const markRead = useMutation({
    mutationFn: (id: string) => notificationService.markRead(id),
    onSuccess: refresh,
  });
  const markAll = useMutation({
    mutationFn: () => notificationService.markAllRead(),
    onSuccess: refresh,
  });

  function onRowClick(n: Notification) {
    if (!n.read) markRead.mutate(n.id);
    const to = pathFor(n);
    setOpen(false);
    if (to) navigate(to);
  }

  const items: Notification[] = list.data?.items ?? [];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn('relative rounded-lg p-2 text-ink-500 hover:bg-ink-50', open && 'bg-ink-50 text-ink-800')}
        aria-label={count > 0 ? `Notifications (${count} unread)` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0"
          />
        </svg>
        {count > 0 && (
          <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red2-500 px-1 text-[9px] font-bold text-white">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Click-away layer. */}
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="absolute right-0 z-40 mt-1 w-80 overflow-hidden rounded-card border border-ink-900/10 bg-white shadow-popover">
            <div className="flex items-center justify-between border-b border-ink-100 px-3.5 py-2.5">
              <span className="text-[12.5px] font-bold text-ink-900">Notifications</span>
              {count > 0 && (
                <button
                  type="button"
                  onClick={() => markAll.mutate()}
                  disabled={markAll.isPending}
                  className="text-[11px] font-semibold text-primary-600 hover:text-primary-700 disabled:opacity-50"
                >
                  Mark all read
                </button>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto">
              {list.isPending ? (
                <p className="px-3.5 py-6 text-center text-[11.5px] text-ink-400">Loading…</p>
              ) : items.length === 0 ? (
                <p className="px-3.5 py-6 text-center text-[11.5px] text-ink-400">You're all caught up.</p>
              ) : (
                <ul className="divide-y divide-ink-100">
                  {items.map((n) => {
                    const linkable = Boolean(pathFor(n));
                    return (
                      <li key={n.id}>
                        <button
                          type="button"
                          onClick={() => onRowClick(n)}
                          className={cn(
                            'flex w-full gap-2.5 px-3.5 py-2.5 text-left transition-colors hover:bg-ink-50',
                            !n.read && 'bg-primary-50/50',
                          )}
                        >
                          <span
                            className={cn(
                              'mt-1 h-1.5 w-1.5 shrink-0 rounded-full',
                              n.read ? 'bg-transparent' : 'bg-primary-500',
                            )}
                            aria-hidden="true"
                          />
                          <span className="min-w-0 flex-1">
                            <span className={cn('block text-[12px] leading-snug', n.read ? 'text-ink-700' : 'font-semibold text-ink-900')}>
                              {n.title}
                            </span>
                            {n.body && <span className="mt-0.5 block truncate text-[11px] text-ink-500">{n.body}</span>}
                            <span className="mt-0.5 block text-[10px] text-ink-400">
                              {formatDate(n.createdAt)}
                              {linkable && ' · View →'}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="border-t border-ink-100">
              <button
                type="button"
                onClick={() => { setOpen(false); navigate('/notifications'); }}
                className="w-full px-3.5 py-2 text-center text-[11.5px] font-semibold text-primary-600 hover:bg-ink-50 hover:text-primary-700"
              >
                View all notifications →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
